<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import ConfirmDialog from '../../components/TwConfirmDialog';

export default {
  name: 'GiftVoucherBookingList',
  components: {
    ConfirmDialog,
  },
  data() {
    return {
      products: [],
      countries: [],
      selected: {},
      allSelected: false,
      notificationText: undefined,
      notification: false,
      domain: undefined,
      arrivalDateFrom: this.$moment()
        .add(8, 'w')
        .format('DD/MM/YY'),
      arrivalDateTo: this.$moment()
        .add(12, 'w')
        .format('DD/MM/YY'),
      headers: [
        { text: 'Booking Ref', value: 'bookingRef' },
        { text: 'Currency', value: 'currency' },
        { text: 'Payment', value: 'totalPrice' },
        { text: 'Product', value: 'product' },
        { text: 'Country', value: 'country' },
        { text: 'Arrival', value: 'arrivalDate', width: '250px' },
        { text: 'Select', sortable: false, width: '25px' },
      ],
      pagination: {
        rowsPerPage: 25,
        page: 1,
        sortBy: 'arrivalDate',
      },
    };
  },
  created() {
    this.setBreadcrumbs({
      breadcrumbs: [
        { text: this.$t('menu.gift-voucher.title'), to: '', disabled: true },
        { text: this.$t('menu.gift-voucher.pages.booking-list.title'), to: this.$route.path },
      ],
      title: this.$t('menu.gift-voucher.pages.booking-list.title'),
    });
  },
  mounted() {
    if (this.domains.items.length === 0 && !this.domains.loading) {
      this.loadDomains();
    }
  },
  methods: {
    ...mapActions({
      setBreadcrumbs: 'nav/setBreadcrumbs',
      loadDomains: 'giftVoucher/loadDomains',
      searchBookings: 'giftVoucher/searchBookingList',
      mailBookings: 'giftVoucher/mailBookings',
    }),
    search() {
      if (!this.validSearchInput) {
        return;
      }
      const request = {
        domainIds: [this.domain],
        arrivalFrom: this.$moment(this.arrivalDateFrom, 'DD/MM/YY')
          .format('Y-MM-DD'),
        arrivalTo: this.$moment(this.arrivalDateTo, 'DD/MM/YY')
          .format('Y-MM-DD'),
      };
      this.searchBookings(request)
        .then(() => {
          if (this.bookings.error) {
            this.notificationText = this.$t('gift-voucher.bookings.error');
            this.notification = true;
          } else if (this.bookings.items.length === 0) {
            this.notificationText = this.$t('gift-voucher.bookings.noContent');
            this.notification = true;
          }
          this.refreshSelected();
        });
    },
    daysToArrival(date) {
      return this.$moment(date, 'Y-MM-DD')
        .diff(this.$moment(), 'days');
    },
    selectAll() {
      if (this.allSelected) {
        this.selected = {};
      } else {
        this.computedBookings.forEach((booking) => {
          this.$set(this.selected, booking.bookingId, true);
        });
      }
    },
    refreshSelected() {
      this.selected = {};
      this.allSelected = false;
    },
    sendMails() {
      if (this.selectedBookingIds.length === 0 || this.sendingMails.loading) {
        return;
      }
      this.mailBookings(this.selectedBookingIds)
        .then(() => {
          if (this.sendingMails.error) {
            this.notificationText = this.$t('gift-voucher.sendEmails.error');
            this.notification = true;
          } else {
            this.refreshSelected();
            this.search();
          }
        });
    },
    getTwentyBookingUrl(bookingId) {
      return `${process.env.TWENTY_FRONT_PAGE}/booking/bookings/${bookingId}/info`;
    },
  },
  computed: {
    ...mapState({
      domains: state => state.giftVoucher.domains,
      bookings: state => state.giftVoucher.bookingList.bookings,
      sendingMails: state => state.giftVoucher.bookingList.sendingMails,
    }),
    ...mapGetters({
      computedDomains: 'giftVoucher/getComputedDomains',
    }),
    validSearchInput() {
      return this.domain && this.arrivalDateFrom && this.arrivalDateTo;
    },
    computedBookings() {
      return this.bookings.items.filter((booking) => {
        if (this.products.length > 0 && !this.products.includes(booking.productTypeName)) {
          return false;
        }
        if (this.countries.length > 0 && !this.countries.includes(booking.countryName)) {
          return false;
        }
        return true;
      });
    },
    computedProducts() {
      if (this.bookings.items.length === 0) {
        return [];
      }
      return this.bookings.items.map(booking => booking.productTypeName);
    },
    computedCountries() {
      if (this.bookings.items.length === 0) {
        return [];
      }
      return this.bookings.items.map(booking => booking.countryName);
    },
    selectedBookingIds() {
      const bookingIds = [];
      Object.keys(this.selected)
        .forEach((select) => {
          if (this.selected[select]) {
            bookingIds.push(select);
          }
        });
      return bookingIds;
    },
  },
};
</script>
<template>
  <v-flex>
    <v-container grid-list>
      <v-layout row wrap>
        <v-flex xs6 pr-5>
          <h2 class="mb-4 text-xs-center">{{ $t('gift-voucher.search') }}</h2>
          <v-select
            v-model="domain"
            :items="computedDomains"
            :loading="domains.loading"
            :disabled="domains.loading || bookings.loading || sendingMails.loading"
            :label="$t('gift-voucher.domains.label')"
            item-value="id"
            item-text="name"
            dense
          />
          <v-text-field
            v-model="arrivalDateFrom"
            :label="$t('gift-voucher.arrivalDates.from.label')"
            :disabled="bookings.loading || sendingMails.loading"
            class="gift-voucher-bookings__dates left"
            mask="##/##/##"
            placeholder="##/##/##"
          ></v-text-field>
          <v-text-field
            v-model="arrivalDateTo"
            :label="$t('gift-voucher.arrivalDates.to.label')"
            :disabled="bookings.loading || sendingMails.loading"
            class="gift-voucher-bookings__dates right"
            mask="##/##/##"
            placeholder="##/##/##"
          ></v-text-field>
          <v-btn
            @click="search"
            :disabled="!validSearchInput || bookings.loading || sendingMails.loading"
            color="info"
            class="gift-voucher-bookings__btn"
          >
            {{ $t('gift-voucher.search') }}
          </v-btn>
        </v-flex>

        <v-flex xs6 pl-5 v-if="bookings.items.length > 0">
          <h2 class="mb-4 text-xs-center">{{ $t('gift-voucher.filter') }}</h2>
          <v-select
            v-model="products"
            @change="refreshSelected"
            :disabled="sendingMails.loading"
            :items="computedProducts"
            :label="$t('gift-voucher.products.label')"
            dense
            multiple
          />
          <v-select
            v-model="countries"
            @change="refreshSelected"
            :disabled="sendingMails.loading"
            :items="computedCountries"
            :label="$t('gift-voucher.countries.label')"
            dense
            multiple
          />
        </v-flex>
      </v-layout>
    </v-container>

    <v-container grid-list>
      <v-layout row>
        <v-flex xs12>
          <v-data-table
            :headers="headers"
            :pagination.sync="pagination"
            :items="computedBookings"
            :loading="bookings.loading || sendingMails.loading"
          >
            <template v-slot:items="props">
              <tr>
                <td>
                  <a :href="getTwentyBookingUrl(props.item.bookingId)" target="_blank">
                    {{ props.item.bookingRef }}
                  </a>
                </td>
                <td>{{ props.item.currency }}</td>
                <td>{{ props.item.totalPaid }} / {{ props.item.totalPrice }}</td>
                <td>{{ props.item.productTypeName }}</td>
                <td>{{ props.item.countryName }}</td>
                <td>
                  {{ props.item.arrivalDate | moment('DD/MM/YY') }}
                  <span class="grey--text text--darken-1 pl-1 font-italic">
                    ({{ daysToArrival(props.item.arrivalDate) }})
                  </span>
                </td>
                <td>
                  <v-checkbox
                    v-model="selected[props.item.bookingId]"
                    @change="allSelected = false"
                    :disabled="sendingMails.loading"
                    :ripple="false"
                    class="gift-voucher-bookings__checkbox"
                  />
                </td>
              </tr>
            </template>
            <template v-if="bookings.items.length > 0" v-slot:footer>
              <tr>
                <td colspan="5"></td>
                <td>
                  {{$t('gift-voucher.bookings.totalSelected')}}: {{selectedBookingIds.length}}
                </td>
                <td>
                  <v-checkbox
                    v-model="allSelected"
                    @click="selectAll"
                    :disabled="sendingMails.loading"
                    :ripple="false"
                    class="gift-voucher-bookings__checkbox"
                  />
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
      <v-layout row>
        <v-flex xs12>
          <v-btn
            @click="$refs.confirm.open()"
            :disabled="Object.keys(selected).length === 0 || sendingMails.loading"
            color="info"
            class="gift-voucher-bookings__btn"
          >
            {{ $t('gift-voucher.send') }}
          </v-btn>
        </v-flex>
      </v-layout>
    </v-container>
    <confirm-dialog
      @confirm="sendMails"
      :title="$t('gift-voucher.confirm.title')"
      :description="$t('gift-voucher.confirm.description')"
      ref="confirm"
    >
    </confirm-dialog>
    <v-snackbar v-model="notification">{{ notificationText }}</v-snackbar>
  </v-flex>
</template>
<style lang="scss">
  .gift-voucher-bookings {
    &__dates {
      width: 45%;
    }

    &__btn {
      float: right;
      margin-right: 0 !important;
    }

    &__checkbox .v-input--selection-controls__input {
      padding-top: 20px;
      padding-left: 10px;

      & > input {
        top: 10px;
      }
    }

    &__table-footer {
      text-align: right;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/gift-vouchers/GiftVoucherBookingList.vue